import {InsertableColumnInfo} from 'notebook/tables/constants/TableColumnConstants';
import {createTableModelSelector} from 'notebook/tables/store/selectors/util/SelectorCreators';
import {TableEditableStore} from 'notebook/tables/store/TableStore';

import {TableContextModel} from './TableContextSelectors';
import {TableDimensionsModel} from './TableDimensionsSelectors';

/**
 * The table-level values a column header renders with. These are identical for every column, so
 * they are collected here rather than threaded through `TableEditable._getHeaderTRProps` to each
 * header. Both the column header row and the Single Input headers read from this model.
 */
export interface TableColumnHeaderModel {
  /** The file the table belongs to, used to resolve column mapping icons. */
  fileId: string;
  /** Whether the table lives in a configuration document, which gates the column locking options. */
  isConfigTable: boolean;
  /** Whether columns may be added to or removed from the table. */
  canAddRemoveColumns: boolean;
  /** Whether the table is locked, which disables column edits. */
  locked: boolean;
  /** Whether the table is rendered in a viewer context (print preview, export), disabling edits. */
  viewer: boolean;
  /** Whether the table is protected, which restricts the column header's context menu. */
  isProtected: boolean;
  /**
   * Whether the column headers should render excess labels (e.g. "A") alongside their custom
   * labels. Turned on while a formula is being edited.
   */
  showExcessLabels: boolean;
  /** Whether the "Lock column" context menu options are suppressed. */
  disableColumnLocking: boolean;
  /**
   * Whether the legacy "header column" toggle is suppressed on the first column header.
   */
  disableLegacyHeader: boolean;
  /** Whether the column headers render left-aligned. */
  isLeftAlignColumnHeader: boolean;
  /** Whether the row header renders left-aligned, which the header row's leading cell matches. */
  isLeftAlignRowHeader: boolean;
  /** Whether the column type / column mapping icons are hidden on the column headers. */
  hideColumnHeaderIcons: boolean;
  /**
   * The number of ghost cells (empty placeholder columns) the header row renders after the real
   * column headers. Always zero in the single-input view, which has no ghost cells.
   */
  ghostCellsCount: number;
  /** The columns offered by the header's "Insert column left/right" submenus. */
  insertableColumnInfos?: Array<InsertableColumnInfo>;
  /**
   * The Single-Input-compatible subset of {@link insertableColumnInfos}, offered by the Single
   * Input header's "Insert single input left/right" submenus so that it only lists columns that
   * can be configured as Single Inputs. Falls back to {@link insertableColumnInfos} when the
   * embedding feature does not distinguish the two.
   */
  singleInputInsertableColumnInfos?: Array<InsertableColumnInfo>;
}

/**
 * Creates a memoized selector that computes the {@link TableColumnHeaderModel} from the store.
 */
export function createTableColumnHeaderSelector(
  tableContextSelector: (store: TableEditableStore) => TableContextModel,
  tableDimensionsSelector: (store: TableEditableStore) => TableDimensionsModel
): (store: TableEditableStore) => TableColumnHeaderModel {
  return createTableModelSelector(
    [
      (store) => store.stableProps.fileId,
      (store) => tableContextSelector(store).isConfigTable,
      (store) => tableDimensionsSelector(store).ghostCellsCount,
      (store) => store.stableProps.showExcessLabels,
      (store) => store.stableProps.locked,
      // The store holds TableEditable's raw props, so the defaults here repeat the ones
      // `TableEditable`'s `propsWithDefaults` applies before rendering the class component.
      (store) => store.stableProps.canAddRemoveColumns ?? true,
      (store) => store.stableProps.viewer ?? false,
      (store) => store.stableProps.isProtected ?? false,
      (store) => store.stableProps.disableColumnLocking ?? false,
      (store) => store.stableProps.disableLegacyHeader ?? false,
      (store) => store.stableProps.isLeftAlignColumnHeader ?? true,
      (store) => store.stableProps.isLeftAlignRowHeader ?? false,
      (store) => store.stableProps.hideColumnHeaderIcons ?? false,
      (store) => store.stableProps.insertableColumnInfos,
      (store) => store.stableProps.singleInputInsertableColumnInfos,
    ],
    (
      fileId,
      isConfigTable,
      ghostCellsCount,
      showExcessLabels,
      locked,
      canAddRemoveColumns,
      viewer,
      isProtected,
      disableColumnLocking,
      disableLegacyHeader,
      isLeftAlignColumnHeader,
      isLeftAlignRowHeader,
      hideColumnHeaderIcons,
      insertableColumnInfos,
      singleInputInsertableColumnInfos
    ) => ({
      fileId,
      isConfigTable,
      ghostCellsCount,
      showExcessLabels,
      locked,
      canAddRemoveColumns,
      viewer,
      isProtected,
      disableColumnLocking,
      disableLegacyHeader,
      isLeftAlignColumnHeader,
      isLeftAlignRowHeader,
      hideColumnHeaderIcons,
      insertableColumnInfos,
      singleInputInsertableColumnInfos: singleInputInsertableColumnInfos ?? insertableColumnInfos,
    })
  );
}
